import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Modal,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import HistoryIcon from "@mui/icons-material/History";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";

import Api from "../../api/Api";
import { Urls } from "../../api/Urls";
import { useAuthContext } from "../../hooks/useAuthContext";
import FormikErrorText from "../../components/ui/FormikErrorText";
import ContentBox from "../../components/ui/ContentBox";
import { modalStyle, modalStyleOverflow } from "../../theme/ModalStyle";

/**
 * UX 16  — single-session detail page.
 * Backend: GET /courtSchedules/{id}/detail
 * Also wires Medium 7 (waitlist) and Medium 8 (coach substitution).
 */
function SessionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const access_token = user?.access_token;
  const headers = useMemo(
    () => ({ "content-type": "application/json", authorization: `Bearer ${access_token}` }),
    [access_token]
  );

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [reload, setReload] = useState(false);

  // modals
  const [openSubstitute, setOpenSubstitute] = useState(false);
  const [openHistory, setOpenHistory] = useState(false);
  const [openWaitlist, setOpenWaitlist] = useState(false);

  // ancillary lists
  const [coaches, setCoaches] = useState([]);
  const [coachChanges, setCoachChanges] = useState([]);
  const [players, setPlayers] = useState([]);

  useEffect(() => {
    document.title = "Session detail";
  }, []);

  useEffect(() => {
    setLoading(true);
    Api.get(Urls.GET_COURT_SCHEDULE_DETAIL + id + "/detail", { headers })
      .then((res) => {
        setData(res?.data?.data || res?.data);
        setLoading(false);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to load session");
        setLoading(false);
      });
  }, [id, reload, headers]);

  const refresh = () => setReload((r) => !r);

  // ───────── Substitute coach ─────────
  const openSubstituteModal = () => {
    Api.get(Urls.GET_COACHES + "?perPage=200", { headers })
      .then((res) => setCoaches(res?.data?.data || []))
      .catch(() => {});
    setOpenSubstitute(true);
  };

  const handleSubstitute = (values) => {
    Api.post(
      Urls.SUBSTITUTE_COACH + id + "/substitute-coach",
      values,
      { headers }
    )
      .then(() => {
        toast.success("Coach substituted");
        setOpenSubstitute(false);
        refresh();
      })
      .catch((err) => toast.error(err?.response?.data?.message || "Substitution failed"));
  };

  // ───────── Coach-change history ─────────
  const openHistoryModal = () => {
    Api.get(Urls.GET_COACH_CHANGES + id + "/coach-changes", { headers })
      .then((res) => setCoachChanges(res?.data?.data || []))
      .catch((err) => toast.error(err?.response?.data?.message || "Failed to load history"));
    setOpenHistory(true);
  };

  // ───────── Waitlist ─────────
  const openWaitlistModal = () => {
    Api.get(Urls.GET_PLAYERS_LIST + "?perPage=200", { headers })
      .then((res) => setPlayers(res?.data?.data || []))
      .catch(() => {});
    setOpenWaitlist(true);
  };

  const handleAddToWaitlist = (values) => {
    Api.post(Urls.WAITLIST_JOIN + id + "/waitlist", values, { headers })
      .then(() => {
        toast.success("Player added to waitlist");
        setOpenWaitlist(false);
        refresh();
      })
      .catch((err) => toast.error(err?.response?.data?.message || "Failed to add"));
  };

  const handleRemoveFromWaitlist = (entryId) => {
    if (!window.confirm("Remove this player from the waitlist?")) return;
    Api.delete(Urls.WAITLIST_LEAVE + id + "/waitlist/" + entryId, { headers })
      .then(() => {
        toast.success("Removed");
        refresh();
      })
      .catch((err) => toast.error(err?.response?.data?.message || "Failed"));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box padding="24px">
        <Typography>No data.</Typography>
      </Box>
    );
  }

  const {
    schedule = {},
    court = {},
    term = {},
    coach = {},
    original_coach = null,
    coach_substituted_at = null,
    program = null,
    levels = [],
    capacity = {},
    roster = [],
    waitlist = [],
    coach_changes = [],
  } = data;

  const isFull = !!capacity?.is_full;

  return (
    <Box padding="24px" display="flex" flexDirection="column" gap="16px">
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" gap="12px">
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography fontSize="22px" fontWeight="600" color="#4B465C">
              Session detail
            </Typography>
            <Typography fontSize="13px" color="#7B7A86">
              {schedule?.start_datetime} → {schedule?.end_datetime}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap="8px">
          <Button variant="primary_outlined" startIcon={<HistoryIcon />} onClick={openHistoryModal}>
            Coach history
          </Button>
          <Button variant="primary_outlined" startIcon={<SwapHorizIcon />} onClick={openSubstituteModal}>
            Substitute coach
          </Button>
          <Button variant="primary_active" startIcon={<GroupAddIcon />} onClick={openWaitlistModal}>
            Add to waitlist
          </Button>
        </Box>
      </Box>

      {/* Summary card */}
      <ContentBox>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Typography fontSize="12px" color="#7B7A86">Court</Typography>
            <Typography fontSize="15px" fontWeight="500">{court?.name || "—"}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography fontSize="12px" color="#7B7A86">Term</Typography>
            <Typography fontSize="15px" fontWeight="500">{term?.name || "—"}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography fontSize="12px" color="#7B7A86">Coach</Typography>
            <Typography fontSize="15px" fontWeight="500">
              {coach?.first_name} {coach?.last_name}
              {coach_substituted_at && (
                <Chip
                  label="substituted"
                  color="warning"
                  size="small"
                  sx={{ ml: 1, height: "18px", fontSize: "10px" }}
                />
              )}
            </Typography>
            {original_coach && (
              <Typography fontSize="11px" color="#7B7A86" sx={{ mt: "2px" }}>
                Original: {original_coach.first_name} {original_coach.last_name}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography fontSize="12px" color="#7B7A86">Capacity</Typography>
            <Typography fontSize="15px" fontWeight="500">
              {capacity?.current_bookings_count ?? 0} / {capacity?.max_players ?? "∞"}
              <Chip
                label={isFull ? "FULL" : `${capacity?.available_seats ?? 0} seats free`}
                color={isFull ? "error" : "success"}
                size="small"
                sx={{ ml: 1, height: "18px", fontSize: "10px" }}
              />
            </Typography>
          </Grid>

          {program && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography fontSize="12px" color="#7B7A86">Program</Typography>
              <Typography fontSize="15px" fontWeight="500">{program?.name}</Typography>
              <Box display="flex" gap="6px" sx={{ mt: 1 }} flexWrap="wrap">
                {(levels || []).map((l) => (
                  <Chip key={l.id} label={l.name} size="small" />
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      </ContentBox>

      {/* Roster */}
      <ContentBox>
        <Typography fontSize="18px" fontWeight="500" sx={{ mb: 1 }}>
          Roster ({roster.length})
        </Typography>
        {roster.length === 0 ? (
          <Typography fontSize="13px" color="#7B7A86">No players enrolled.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Player</TableCell>
                <TableCell>Level</TableCell>
                <TableCell>Attendance</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roster.map((p) => (
                <TableRow key={p.id || p.player_id}>
                  <TableCell>{p.first_name} {p.last_name}</TableCell>
                  <TableCell>{p.level?.name || "—"}</TableCell>
                  <TableCell>
                    {p.attendance_status ? (
                      <Chip
                        label={p.attendance_status}
                        size="small"
                        color={
                          p.attendance_status === "present"
                            ? "success"
                            : p.attendance_status === "absent"
                            ? "error"
                            : "default"
                        }
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>{p.attendance_notes || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ContentBox>

      {/* Waitlist */}
      <ContentBox>
        <Typography fontSize="18px" fontWeight="500" sx={{ mb: 1 }}>
          Waitlist ({waitlist.length})
        </Typography>
        {waitlist.length === 0 ? (
          <Typography fontSize="13px" color="#7B7A86">No-one waiting.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Player</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell>Notified</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {waitlist.map((w) => (
                <TableRow key={w.id}>
                  <TableCell>{w.position}</TableCell>
                  <TableCell>{w.player?.first_name} {w.player?.last_name}</TableCell>
                  <TableCell>
                    <Chip
                      label={w.status}
                      size="small"
                      color={
                        w.status === "notified" ? "warning"
                        : w.status === "promoted" ? "success"
                        : w.status === "expired" || w.status === "cancelled" ? "default"
                        : "info"
                      }
                    />
                  </TableCell>
                  <TableCell>{w.created_at || "—"}</TableCell>
                  <TableCell>{w.notified_at || "—"}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Remove">
                      <IconButton size="small" onClick={() => handleRemoveFromWaitlist(w.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ContentBox>

      {/* Inline coach-change history */}
      {coach_changes && coach_changes.length > 0 && (
        <ContentBox>
          <Typography fontSize="18px" fontWeight="500" sx={{ mb: 1 }}>
            Coach changes
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Previous</TableCell>
                <TableCell>New</TableCell>
                <TableCell>By</TableCell>
                <TableCell>Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coach_changes.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{c.changed_at}</TableCell>
                  <TableCell>{c.previous_coach?.first_name} {c.previous_coach?.last_name}</TableCell>
                  <TableCell>{c.new_coach?.first_name} {c.new_coach?.last_name}</TableCell>
                  <TableCell>{c.changed_by?.first_name} {c.changed_by?.last_name}</TableCell>
                  <TableCell>{c.reason || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ContentBox>
      )}

      {/* ───── Substitute coach modal ───── */}
      <Modal open={openSubstitute} onClose={() => setOpenSubstitute(false)}>
        <Box sx={modalStyle}>
          <Typography fontSize="18px" fontWeight="600" sx={{ mb: 2 }}>
            Substitute coach for this session
          </Typography>
          <Formik
            initialValues={{ new_coach_id: "", reason: "" }}
            validationSchema={yup.object({
              new_coach_id: yup.number().required("Select a coach"),
              reason: yup.string().max(255),
            })}
            onSubmit={handleSubstitute}
          >
            {({ values, setFieldValue }) => (
              <Form>
                <Box display="flex" flexDirection="column" gap="12px">
                  <Field
                    as={TextField}
                    select
                    name="new_coach_id"
                    label="New coach"
                    fullWidth
                    size="small"
                    onChange={(e) => setFieldValue("new_coach_id", e.target.value)}
                    value={values.new_coach_id}
                  >
                    {coaches
                      .filter((c) => c.id !== coach?.id)
                      .map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.first_name} {c.last_name}
                        </MenuItem>
                      ))}
                  </Field>
                  <ErrorMessage component={FormikErrorText} name="new_coach_id" />

                  <Field
                    as={TextField}
                    name="reason"
                    label="Reason (optional)"
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                  />

                  <Box display="flex" gap="8px" justifyContent="flex-end" sx={{ mt: 1 }}>
                    <Button variant="primary_outlined" onClick={() => setOpenSubstitute(false)}>
                      Cancel
                    </Button>
                    <Button variant="primary_active" type="submit">Substitute</Button>
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>

      {/* ───── Coach-change history modal ───── */}
      <Modal open={openHistory} onClose={() => setOpenHistory(false)}>
        <Box sx={{ ...modalStyleOverflow }}>
          <Typography fontSize="18px" fontWeight="600" sx={{ mb: 2 }}>
            Coach-change history
          </Typography>
          {coachChanges.length === 0 ? (
            <Typography fontSize="13px" color="#7B7A86">No substitutions recorded.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Previous</TableCell>
                  <TableCell>New</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coachChanges.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.changed_at}</TableCell>
                    <TableCell>{c.previous_coach?.first_name} {c.previous_coach?.last_name}</TableCell>
                    <TableCell>{c.new_coach?.first_name} {c.new_coach?.last_name}</TableCell>
                    <TableCell>{c.changed_by?.first_name} {c.changed_by?.last_name}</TableCell>
                    <TableCell>{c.reason || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="primary_outlined" onClick={() => setOpenHistory(false)}>Close</Button>
          </Box>
        </Box>
      </Modal>

      {/* ───── Add to waitlist modal ───── */}
      <Modal open={openWaitlist} onClose={() => setOpenWaitlist(false)}>
        <Box sx={modalStyle}>
          <Typography fontSize="18px" fontWeight="600" sx={{ mb: 2 }}>
            Add player to waitlist
          </Typography>
          <Formik
            initialValues={{ player_id: "" }}
            validationSchema={yup.object({
              player_id: yup.number().required("Select a player"),
            })}
            onSubmit={handleAddToWaitlist}
          >
            {({ values, setFieldValue }) => (
              <Form>
                <Box display="flex" flexDirection="column" gap="12px">
                  <Field
                    as={TextField}
                    select
                    name="player_id"
                    label="Player"
                    fullWidth
                    size="small"
                    onChange={(e) => setFieldValue("player_id", e.target.value)}
                    value={values.player_id}
                  >
                    {players.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.first_name} {p.last_name}
                      </MenuItem>
                    ))}
                  </Field>
                  <ErrorMessage component={FormikErrorText} name="player_id" />

                  <Box display="flex" gap="8px" justifyContent="flex-end" sx={{ mt: 1 }}>
                    <Button variant="primary_outlined" onClick={() => setOpenWaitlist(false)}>
                      Cancel
                    </Button>
                    <Button variant="primary_active" type="submit">Add</Button>
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>
    </Box>
  );
}

export default SessionDetail;
